import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'store';
import { fetcher } from 'utils/axios';
import type { CompanyFacebookNotification } from 'types/company/company';
import { evaluateHeaderSettingsAlerts, type HeaderSettingsAlertIssue } from 'utils/headerSettingsAlert';

const POLL_MS = 5 * 60 * 1000;

export type HeaderSettingsAlertState = {
  /** מציג אייקון אזהרה על סטטוס הגדרות בכותרת */
  showBadge: boolean;
  /** רשימת בעיות פעילות — כל פריט עם messageId ו-fixPath משלו */
  issues: HeaderSettingsAlertIssue[];
};

export function useHeaderSettingsAlert(): HeaderSettingsAlertState {
  const company = useSelector((s) => s.company);
  const applicationUsers = useSelector((s) => s.applicationUsers);
  const currentUserRole = useSelector((s) => s.user?.Role);
  const messagesQuota = useSelector((s) => s.accountModules.data?.messagesQuota ?? null);
  const [notifications, setNotifications] = useState<CompanyFacebookNotification[]>([]);

  const refresh = useCallback(async () => {
    try {
      const data = await fetcher('get', '/api/services/getFacebookNotifications', 'priority');
      setNotifications(Array.isArray(data) ? data : []);
    } catch {
      setNotifications([]);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  return useMemo(() => {
    const issues = evaluateHeaderSettingsAlerts({
      company,
      applicationUsers,
      facebookNotifications: notifications,
      currentUserRole,
      messagesQuota,
    });

    return {
      showBadge: issues.length > 0,
      issues,
    };
  }, [applicationUsers, company, currentUserRole, messagesQuota, notifications]);
}
